import type { DatesParam } from '~/utils/datetime'
import type { FormatterOptions } from '~/utils/dates/formatters'

import {
  formatAsDateISO,
  formatAsDateMonth,
  formatAsDateMonthWithTime,
  formatAsDayAndMonth,
  formatAsDayOfWeek,
  formatAsFullDateLong,
  formatAsFullMonthYear,
  formatAsHour12Abbreviated,
  formatAsHour24,
  formatAsISOString,
  formatAsMonthName,
  formatAsMonthYear,
  formatAsOrdinalDateLong,
  formatAsShortDate,
  formatAsShortDateRange,
  formatAsShortDateRangeWithYear,
  formatAsShortDateRangeWithYears,
  formatAsShortDateWithTime,
  formatAsShortDateYear,
  formatAsTime,
  formatAsTimeZone,
  formatAsYearOnly,
  translateTime,
} from '~/utils/dates/formatters'

/**
 * Use the dates composable to format dates and access the user's timezone.
 *
 * It's important to note that dates formatted for the user's local time, should
 * always use the user's timezone and locale explicitly, otherwise the defaults
 * ('en' and 'UTC') will be used.
 *
 * The same goes for dates that need to be formatted in a specific locale. Since
 * the user's locale will be used by default, it would need to be explicitly
 * set to overwrite the default.
 *
 * @example formatAsShortDateWithTime('2025-08-29T13:00:00Z', { timezone: userTimezone }) // 8/29/2025 09:00 AM (America/New_York)
 * @example formatAsShortDateWithTime('2025-08-29T13:00:00Z') // 8/29/2025 01:00 PM (UTC)
 * @example formatAsShortDateWithTime('2025-08-29T13:00:00Z', { locale: 'fr', timezone: userTimezone }) // 29 août 2025 09:00 (America/New_York)
 * @example formatAsShortDateWithTime('2025-08-29T13:00:00Z', { locale: 'fr') // 29 août 2025 13:00 (UTC)
 */
export default function useDates() {
  const { locale: computedLocale } = useI18n({
    useScope: 'global',
  })
  const { geolocation } = useGeolocation()
  const computedTimezone = computed(() => geolocation.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/New_York')

  return {
    userTimezone: computedTimezone,
    formatAsDateISO: (date?: DatesParam, options?: FormatterOptions) => formatAsDateISO(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsDateMonth: (date?: DatesParam, options?: FormatterOptions) => formatAsDateMonth(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsDateMonthWithTime: (date?: DatesParam, options?: FormatterOptions) => formatAsDateMonthWithTime(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsDayAndMonth: (date?: DatesParam, options?: FormatterOptions) => formatAsDayAndMonth(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsDayOfWeek: (date?: DatesParam, options?: FormatterOptions) => formatAsDayOfWeek(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsFullDateLong: (date?: DatesParam, options?: FormatterOptions) => formatAsFullDateLong(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsFullMonthYear: (date?: DatesParam, options?: FormatterOptions) => formatAsFullMonthYear(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsHour12Abbreviated: (date?: DatesParam, options?: FormatterOptions) => formatAsHour12Abbreviated(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsHour24: (date?: DatesParam, options?: FormatterOptions) => formatAsHour24(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsISOString: (date?: DatesParam, options?: FormatterOptions) => formatAsISOString(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsMonthYear: (date?: DatesParam, options?: FormatterOptions) => formatAsMonthYear(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsOrdinalDateLong: (date?: DatesParam, options?: FormatterOptions) => formatAsOrdinalDateLong(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDate: (date?: DatesParam, options?: FormatterOptions) => formatAsShortDate(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDateRange: (startDate: DatesParam, endDate: DatesParam, options?: FormatterOptions) => formatAsShortDateRange(startDate, endDate, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDateRangeWithYear: (startDate: DatesParam, endDate: DatesParam, options?: FormatterOptions) => formatAsShortDateRangeWithYear(startDate, endDate, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDateRangeWithYears: (startDate: DatesParam, endDate: DatesParam, options?: FormatterOptions) => formatAsShortDateRangeWithYears(startDate, endDate, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDateWithTime: (date?: DatesParam, options?: FormatterOptions) => formatAsShortDateWithTime(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsShortDateYear: (date?: DatesParam, options?: FormatterOptions) => formatAsShortDateYear(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsTime: (date?: DatesParam, options?: FormatterOptions) => formatAsTime(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsTimeZone: (date?: DatesParam, options?: FormatterOptions) => formatAsTimeZone(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    formatAsMonthName: (monthNumber: number, options?: Pick<FormatterOptions, 'locale'>) => formatAsMonthName(monthNumber, { locale: options?.locale ?? computedLocale.value }),
    formatAsYearOnly: (date?: DatesParam, options?: FormatterOptions) => formatAsYearOnly(date, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
    translateTime: (value: number, formatIsUppercase: boolean = true, options?: FormatterOptions) => translateTime(value, formatIsUppercase, { locale: options?.locale ?? computedLocale.value, timezone: options?.timezone }),
  }
}
